import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../../api/store";
import { Affiliation, BasicInformationState, ClientBasicInfoState } from "./basic-information.state";
import { BasicInfoContacts, BasicInformationValue, OpenTypeOfOptions } from "./basic-information.types";
import { v4 as uuidv4 } from "uuid";
import { SubTypeOption } from "../../../api/gms/group-category/group-category-api-state";

export const createEmptyAffiliation = (): Affiliation => ({
    id: uuidv4(),
    name: "",
    address: "",
    tinNumber: '',
});

const initialState: BasicInformationState = {
    basicInformation: {
        groupName: "",
        noOfLives: 0,
        companyMobileNo: "",
        companyTelephone: "",
        completeBusinessAddress: "",
        proposalAddress: "",
        email: "",
        addressDesignation: "",
        designation: "",
        contactPersonSalutation: "",
        contactPersonFirstName: "",
        contactPersonMiddleName: "",
        contactPersonLastName: "",
        contactNumber: "",
        contactTelephone: "",
        tinNumber: "",
        secNumber: "",
    },
    companyNumber: null,
    companyMobileNumber: null,
    contactPersonMobileNumber: null,
    isOpenTypeOfOptions: null,

    affiliation: [],
    hasAffiliation: false,

    selectedNatureOfBusinessCategory: null,
    selectedNatureOfBusinessItem: null,

    effectiveDate: '',
};

const numericFields: (keyof ClientBasicInfoState)[] = [
    "noOfLives",
];

const basicInformationSlice = createSlice({
    name: "basicInformationSlice",
    initialState,
    reducers: {
        // EFFECTIVE DATE
        setEffectiveDateGMS: (state, action: PayloadAction<string>) => {
            state.effectiveDate = action.payload;
        },
        // BASIC INFO
        setOpenTypeOfOptions: (state, action: PayloadAction<OpenTypeOfOptions>) => {
            state.isOpenTypeOfOptions = action.payload
        },
        setCompanyNumber: (state, action: PayloadAction<{type: BasicInfoContacts; value: SubTypeOption | null}>) => {
            const { type, value} = action.payload
            if(type === 'comNumber'){
                state.companyNumber = value;
            }
            if(type === 'comContactNumber'){
                state.companyMobileNumber = value;
            }
            if(type === 'contactPersonNumber'){
                state.contactPersonMobileNumber = value;
            }
        },
        setBasicInformation: (state, action: PayloadAction<{ field: keyof ClientBasicInfoState; value: BasicInformationValue; }>) => {
            const { field, value } = action.payload;
            // Allow clearing input
            if (value === "") {
                (state.basicInformation as any)[field] = "";
                return;
            }
            // Numeric fields
            if (numericFields.includes(field)) {
                state.basicInformation.noOfLives = Number(value);
                return;
            }
            // Contact/mobile number fields
            if (  field === "contactNumber" || field === "companyMobileNo" ) {
                const digitsOnly = value.toString().replace(/\D/g, "");
                // Enforce starting with 9
                if ( digitsOnly.length === 1 && digitsOnly !== "9" ) {
                    return;
                }
                const formatted = digitsOnly.slice(0, 10);
                (state.basicInformation as any)[field] = formatted;
                return;
            }

            // Other fields including TIN and SEC
            (state.basicInformation as any)[field] = value;
        },
        resetBasicInformation: () => initialState,
        setForm: ( state, action: PayloadAction<ClientBasicInfoState> ) => {
            state.basicInformation = action.payload;
        },

        // AFFILIATION
        setHasAffiliation: (state, action: PayloadAction<boolean>) => {
            state.hasAffiliation = action.payload;
        },
        addAffiliation: (state) => {
            state.affiliation.push(createEmptyAffiliation());
        },
        removeAffiliation: (state, action: PayloadAction<string>) => {
            state.affiliation = state.affiliation.filter(
                item => item.id !== action.payload
            );

            if (state.affiliation.length === 0) {
                state.affiliation.push(createEmptyAffiliation());
            }
        },
        populateAffiliation: (state, action: PayloadAction<Affiliation[]>) => {
            state.affiliation =
                action.payload.length > 0
                    ? action.payload
                    : [createEmptyAffiliation()];
        },
        updateAffiliation: ( state, action: PayloadAction<{ id: string; field: keyof Omit<Affiliation, "id">; value: string | number | null; }> ) => {
            const { id, field, value } = action.payload;

            const affiliation = state.affiliation.find(
                item => item.id === id
            );

            if (affiliation) {
                affiliation[field] = value as never;
            }
        },
        clearAffiliations: (state) => {
            state.affiliation = [];
            state.hasAffiliation = false;
        },
        // nature of business
        setNatureOfBusiness: (state, action: PayloadAction<{ value: SubTypeOption, type: 'category' | 'item'}>) => {
            const { value, type } = action.payload;
            if(type === 'category') {
                state.selectedNatureOfBusinessCategory = value;
            }

            if(type === 'item') {
                state.selectedNatureOfBusinessItem = value;
            }
        },
        resetNatureOfBusiness: (state, action: PayloadAction<{ type: 'category' | 'item'}>) => {
            const { type } = action.payload;
            if(type === 'category') {
                state.selectedNatureOfBusinessCategory = null;
            }

            if(type === 'item') {
                state.selectedNatureOfBusinessItem = null;
            }
        },
    },
});

export const {
    setEffectiveDateGMS,

    setOpenTypeOfOptions,
    setCompanyNumber,
    setBasicInformation,
    resetBasicInformation,
    setForm,
    // AFFILIATION
    setHasAffiliation,
    addAffiliation,
    removeAffiliation,
    populateAffiliation,
    updateAffiliation,
    clearAffiliations,
    // nature of business
    setNatureOfBusiness,
    resetNatureOfBusiness,
} = basicInformationSlice.actions;

export const selectEffectiveDateGMS = (state: RootState) => state.basicInformationSlice.effectiveDate;

export const selectBasicInformation = (state: RootState) => state.basicInformationSlice.basicInformation;

export const selectBasicInformationCompanyNumber = (state: RootState) => state.basicInformationSlice.companyNumber;

export const selectBasicInformationCompanyContactNumber = (state: RootState) => state.basicInformationSlice.companyMobileNumber;

export const selectBasicInformationContactPersonNumber = (state: RootState) => state.basicInformationSlice.contactPersonMobileNumber;

export const selectTypeOfOptions = (state: RootState) => state.basicInformationSlice.isOpenTypeOfOptions;
// AFFILIATION
export const selectAffiliation = (state: RootState) => state.basicInformationSlice.affiliation;
export const selectHasAffiliation = (state: RootState) => state.basicInformationSlice.hasAffiliation;
// nature of business
export const selectedNatureOfBusinessCategory = (state: RootState) => state.basicInformationSlice.selectedNatureOfBusinessCategory

export const selectedNatureOfBusinessItem = (state: RootState) => state.basicInformationSlice.selectedNatureOfBusinessItem

export default basicInformationSlice.reducer;